import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';

import type {
  ConsentAutomationDTO,
  ConsentDeleteDTO,
  ConsentDTO,
  ConsentScopePayload,
} from '@nora-monorepo/nora-api-client';
import { ConsentScopeTypeEnum, ConsentStatusEnum } from '@nora-monorepo/nora-api-client';
import { ensureValidDate, isNonEmptyString } from '@nora-monorepo/shared';
import {
  appLogger,
  ConnectivityStatus,
  useApiStore,
  useGenericAppStore,
  useGenericUserStore,
  UserLoggingContext,
  useWebViewStore,
} from '@nora-monorepo/ui-shared';

export const useConsentStore = defineStore('consentStore', () => {
  const consents = ref<ConsentDTO[]>([]);
  const consentAutomations = ref<ConsentAutomationDTO | undefined>();
  const consentsAreLoaded = ref<boolean>(false);
  const consentsLoadedForUserId = ref<string>();
  const consentAutomationsAreLoaded = ref<boolean>(false);
  const api = useApiStore();
  const userStore = useGenericUserStore();
  const appStore = useGenericAppStore();
  const webViewStore = useWebViewStore();

  // Consent events may have been missed while disconnected. Revalidate before feedback resumes.
  watch(() => appStore.connectivityStatus, (status) => {
    if (webViewStore.isWebView) {
      return;
    }
    if (status !== ConnectivityStatus.ONLINE) {
      consentsLoadedForUserId.value = undefined;
    }
    else if (consentsAreLoaded.value) {
      void loadConsents();
    }
  }, { flush: 'sync' });

  async function loadConsents(): Promise<void> {
    const userId = userStore.currentUserId;
    consentsLoadedForUserId.value = undefined;

    try {
      const myConsents = await api.unwrap(api.consent.getMyConsents());

      if (!myConsents.data) {
        throw new Error('No consents returned');
      }

      if (userId === userStore.currentUserId) {
        consents.value = myConsents.data;
        consentsLoadedForUserId.value = userId;
      }
    }
    catch (error) {
      appLogger.error({
        message: 'Error fetching consents',
        sensitiveContext: {
          error: {
            message: error instanceof Error ? error.message : 'Unknown error',
            name: error instanceof Error ? error.name : 'Unknown error',
            stack: error instanceof Error ? error.stack : 'No stack trace',
          },
        },
        context: {
          user: new UserLoggingContext({ userId: userStore.user?.id ?? 'Unknown', username: userStore.user?.username ?? 'Unknown' }),
        },
      });
    }

    consentsAreLoaded.value = true;
  }

  /**
   * Sharing feedback requires a successful owner-specific load; expiry is rechecked
   * by the caller at the returned deadline, without relying on a backend event.
   */
  function getOwnLocationSharingStatus(now = Date.now()): { active: boolean; nextExpiry?: number } {
    const userId = userStore.currentUserId;
    if (!isNonEmptyString(userId) || consentsLoadedForUserId.value !== userId) {
      return { active: false };
    }

    let active = false;
    let nextExpiry: number | undefined;
    for (const consent of consents.value) {
      if (consent.userId !== userId || consent.status !== ConsentStatusEnum.ACCEPTED) {
        continue;
      }
      for (const scope of consent.scopes) {
        if (scope.scope !== ConsentScopeTypeEnum.LOCATION) {
          continue;
        }
        if (scope.expiresAt == null) {
          active = true;
          continue;
        }
        const expiresAt = ensureValidDate(scope.expiresAt)?.getTime();
        if (expiresAt === undefined || expiresAt <= now) {
          continue;
        }
        active = true;
        nextExpiry = Math.min(nextExpiry ?? expiresAt, expiresAt);
      }
    }
    return { active, nextExpiry };
  }

  // Called whenever we receive a new invite event from backend
  const eventInviteReceived = async (): Promise<void> => {
    await loadConsents();
  };

  const addConsent = (newConsent: ConsentDTO): void => {
    consents.value.push(newConsent);
  };

  const eventCreateConsent = async (consent: ConsentDTO): Promise<void> => {
    if (!consents.value.some(({ id }) => id === consent.id)) {
      addConsent(consent);
    }
  };

  const eventUpdateConsent = async (consent: ConsentDTO): Promise<void> => {
    const consentIndex = consents.value.findIndex(
      ({ id }) => id === consent.id,
    );
    if (consentIndex !== -1) {
      consents.value.splice(consentIndex, 1, consent);
    }
    else if (consent.userId === userStore.currentUserId) {
      addConsent(consent);
    }
  };

  const eventDeleteConsent = async (consent: ConsentDeleteDTO): Promise<void> => {
    const consentIndex = consents.value.findIndex(
      ({ id }) => id === consent.id,
    );

    if (consentIndex !== -1) {
      consents.value.splice(consentIndex, 1);
    }
  };

  const overrideConsentInvitation = async (consentId: string): Promise<void> => {
    await api.consent.overrideConsent({ consentId });
  };

  const overrideConsentScope = async (
    consentId: string,
    consentScopePayload: ConsentScopePayload,
  ): Promise<void> => {
    await api.consent.overrideConsentScope({ consentId, consentScopePayload });
  };

  const deleteAutomationUserConsent = async (
    autoConsent: ConsentAutomationDTO,
  ): Promise<void> => {
    try {
      if (consentAutomations.value) {
        consentAutomations.value.users = consentAutomations.value.users.filter(
          consentAutomation => consentAutomation.id !== autoConsent.id,
        );
      }
    }
    catch (error) {
      console.error('Error deleting automation user consent: ', error);
    }
  };

  const addConsentScope = async (consentId: string, consentScopePayload: ConsentScopePayload): Promise<void> => {
    try {
      await api.consent.addConsentScope({ consentId, consentScopePayload });
    }
    catch (error) {
      console.error('Error while creating a consent scope', error);
    }
  };

  const revokeConsentScope = async (consentId: string, consentScopeId: string): Promise<boolean> => {
    try {
      await api.consent.revokeConsentScope({ consentId, consentScopeId });
      return true;
    }
    catch (error) {
      console.error('Error while revoking consent scope', error);
      return false;
    }
  };

  const deleteAutomationGroupConsent = async (
    autoConsent: ConsentAutomationDTO,
  ): Promise<void> => {
    try {
      if (consentAutomations.value) {
        consentAutomations.value.groups
          = consentAutomations.value.groups.filter(
            consentAutomation => consentAutomation.id !== autoConsent.id,
          );
      }
    }
    catch (error) {
      console.error('Error deleting automation group consent: ', error);
    }
  };

  async function loadConsentAutomations(): Promise<void> {
    try {
      const myConsentAutomations = await api.unwrap(
        api.consent.getMyConsentAutomations(),
      );

      // TODO: Show an error if we can't fetch consent automations
      if (!myConsentAutomations.data) {
        console.error('No consent automations returned');
      }

      consentAutomations.value = myConsentAutomations.data;
    }
    catch (error) {
      console.error('Error fetching consent automations: ', error);
    }

    consentAutomationsAreLoaded.value = true;
  }

  async function bootstrap(): Promise<void> {
    await Promise.all([loadConsents(), loadConsentAutomations()]);
  }

  const getConsent = computed(() => {
    return (consentId?: string): ConsentDTO | undefined => {
      if (!isNonEmptyString(consentId)) {
        return undefined;
      }
      return consents.value.find(consent => consent.id === consentId);
    };
  });

  const userIsSharingLocation = computed(() => {
    return (consent: ConsentDTO): boolean => {
      const hasLocationScope = consent.scopes?.some(
        scope => scope.scope === ConsentScopeTypeEnum.LOCATION,
      );

      if (!hasLocationScope) {
        return false;
      }

      return true;
    };
  });

  const toggleUserSharingLocation = async (consent: ConsentDTO): Promise<void> => {
    const locationConsentScope: ConsentScopePayload = {
      scope: ConsentScopeTypeEnum.LOCATION,
    };

    const hasLocationScope = consent?.scopes?.find(
      scope => scope.scope === ConsentScopeTypeEnum.LOCATION,
    );

    if (userIsSharingLocation.value(consent) && hasLocationScope) {
      await revokeConsentScope(consent.id, hasLocationScope.id);
    }
    else {
      await addConsentScope(consent.id, locationConsentScope);
    }
  };

  const deleteConsentAutomationUser = async (userId: string): Promise<void> => {
    try {
      await api.consent.deleteConsentAutomationUser({
        consentAutomationUserId: userId,
      });
      if (consentAutomations.value) {
        consentAutomations.value.users = consentAutomations.value.users.filter(
          consentAutomation => consentAutomation.id !== userId,
        );
      }
    }
    catch (error) {
      console.error('Error deleting consent automation: ', error);
    }
  };

  const deleteConsentAutomationGroup = async (groupId: string): Promise<void> => {
    try {
      await api.consent.deleteConsentAutomationGroup({
        consentAutomationGroupId: groupId,
      });
      if (consentAutomations.value) {
        consentAutomations.value.groups = consentAutomations.value.groups.filter(
          consentAutomation => consentAutomation.id !== groupId,
        );
      }
    }
    catch (error) {
      console.error('Error deleting consent automation: ', error);
    }
  };

  const rejectConsent = async (consentId: string): Promise<void> => {
    try {
      await api.consent.rejectConsent({ consentId });
      // NOTE: Most other functions here mutate local state directly.
      // Not sure if loading consents remotely makes sense, but I am wary of being out of sync with backend.
      await loadConsents();
    }
    catch (error) {
      console.error('Error declining consent', error);
    }
  };

  const reinviteUser = async (consentId: string): Promise<void> => {
    try {
      await api.consent.reinviteUser({ consentId });
    }
    catch (error) {
      console.error('Error while reinviting user', error);
    }
  };

  const setConsentExpirationDate = async (
    consentId: string,
    consentScopeId: string,
    date: Date,
  ): Promise<void> => {
    try {
      await api.consent.setConsentExpirationDate({
        consentId,
        consentScopeId,
        setConsentExpirationDateDTO: {
          expirationDate: date,
        },
      });

      const consent = getConsent.value(consentId);
      const consentScope = consent?.scopes.find(
        scope => scope.id === consentScopeId,
      );

      if (consentScope) {
        consentScope.expiresAt = date;
      }
    }
    catch (error) {
      console.error('Error setting consent expiration date: ', error);
    }
  };

  const resetData = (): void => {
    consents.value = [];
    consentAutomations.value = undefined;
    consentsAreLoaded.value = false;
    consentsLoadedForUserId.value = undefined;
    consentAutomationsAreLoaded.value = false;
  };

  return {
    bootstrap,
    consents,
    consentAutomations,
    eventCreateConsent,
    eventUpdateConsent,
    eventDeleteConsent,
    eventInviteReceived,
    deleteAutomationUserConsent,
    deleteAutomationGroupConsent,
    addConsent,
    addConsentScope,
    revokeConsentScope,
    getConsent,
    loadConsents,
    overrideConsentInvitation,
    overrideConsentScope,
    deleteConsentAutomationGroup,
    deleteConsentAutomationUser,
    consentsAreLoaded,
    consentsLoadedForUserId,
    getOwnLocationSharingStatus,
    consentAutomationsAreLoaded,
    rejectConsent,
    reinviteUser,
    toggleUserSharingLocation,
    userIsSharingLocation,
    resetData,
    setConsentExpirationDate,
  };
});
